import { useCallback, useMemo } from 'react'

import { Box, Typography } from '@mui/material'
import {
  DistributedTaskLogEntry,
  DistributedTaskLogEntryLevelEnum,
  DistributedTaskLogEntryTypeEnum,
} from '@nativewaves/nw-cloud-sdk/content'
import { useNavigate } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { format, fromUnixTime } from 'date-fns'
import { omit, upperFirst } from 'lodash-es'

import {
  LevelVisualizerButton,
  TypeVizualizerButton,
  AlertIdBadge,
  DescriptionVizualizer,
  StatusVizualizerButton,
} from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Monitoring'
import { avcTaskMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'

export const useColumns = () => {
  const search = avcTaskMonitoringRoute.useSearch()
  const navigate = useNavigate({ from: avcTaskMonitoringRoute.fullPath })

  const handleTypeFilter = useCallback(
    (types: DistributedTaskLogEntryTypeEnum[]) => {
      const currentTypes = new Set(search.types || [])

      types.forEach((type) => (currentTypes.has(type) ? currentTypes.delete(type) : currentTypes.add(type)))

      const newTypes = Array.from(currentTypes)

      if (newTypes.length) {
        navigate({ search: { ...search, types: newTypes } })
      } else {
        navigate({ search: { ...omit(search, 'types') } })
      }
    },
    [navigate, search],
  )

  const handleLevelButton = useCallback(
    (level: DistributedTaskLogEntryLevelEnum) => {
      const currentLevels = new Set(search.levels || [])

      if (currentLevels.has(level)) {
        currentLevels.delete(level)
      } else {
        currentLevels.add(level)
      }

      const newLevels = Array.from(currentLevels)

      if (newLevels.length) {
        navigate({ search: { ...search, levels: newLevels } })
      } else {
        navigate({ search: { ...omit(search, 'levels') } })
      }
    },
    [navigate, search],
  )

  const handleAlertIdButton = useCallback(
    (alertId: string) => {
      if (alertId !== search.alertId) {
        navigate({ search: { ...search, alertId } })
      } else {
        navigate({ search: { ...omit(search, 'alertId') } })
      }
    },
    [navigate, search],
  )

  const handleAlertCodeButton = useCallback(
    (alertCode: string) => {
      if (alertCode !== search.alertCode) {
        navigate({ search: { ...search, alertCode } })
      } else {
        navigate({ search: { ...omit(search, 'alertCode') } })
      }
    },
    [navigate, search],
  )

  return useMemo<ColumnDef<DistributedTaskLogEntry>[]>(
    () => [
      {
        accessorFn: (row) => ({
          type: row.type,
          description: row.message,
          timestamp: row.timestamp,
          level: row.level,
          alertId: row.alertId,
          alertCode: row.alertCode,
        }),
        header: 'Entry',
        meta: {
          sizing: {
            minWidth: 300,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const { description, timestamp, level, type, alertId, alertCode } = getValue<{
            type: DistributedTaskLogEntry['type']
            description: DistributedTaskLogEntry['message']
            timestamp: DistributedTaskLogEntry['timestamp']
            level: DistributedTaskLogEntry['level']
            alertId: DistributedTaskLogEntry['alertId']
            alertCode: DistributedTaskLogEntry['alertCode']
          }>()
          return (
            <Box sx={{ maxWidth: '100%', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', mb: 1, gap: 2 }}>
                <Typography variant="heading-sm">{format(fromUnixTime(timestamp), 'yyyy-MM-dd | HH:mm:ss')}</Typography>
                <LevelVisualizerButton
                  level={level}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleLevelButton(level)
                  }}
                  checked={!!search.levels?.length}
                >
                  <Typography variant="text-sm">{upperFirst(level.toLowerCase())}</Typography>
                </LevelVisualizerButton>
                <TypeVizualizerButton logType={type} onClick={handleTypeFilter} checked={!!search.types?.length} />
                <AlertIdBadge alertId={alertId} onClick={handleAlertIdButton} checked={!!search.alertId} />
                <StatusVizualizerButton logType={type} onClick={handleTypeFilter} checked={!!search.types?.length} />
              </Box>
              <DescriptionVizualizer
                description={description}
                alertCode={alertCode}
                onCodeClick={handleAlertCodeButton}
                checked={!!search.alertCode}
              />
            </Box>
          )
        },
      },
    ],
    [search, handleTypeFilter, handleAlertIdButton, handleAlertCodeButton, handleLevelButton],
  )
}
